import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import cn from 'classnames';
import PasswordValidator from 'password-validator';
import useSWR from 'swr';
import { debounce } from 'lodash';
import { Collapse, Fade, Checkbox } from '@mui/material';
import { validate as isValidEmail } from 'email-validator';
import { Pathname } from 'Routes';
import { Button } from 'Components';
import { encryptedKeyForCredentials, GOOGLE_RECAPTCHA_KEY, CLOUDFLARE_TURNSTILE_SITE_KEY } from 'Commons';
import { AppContext, AuthContext } from 'Context';
import { useSelector, useDispatch } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import { isMobile } from 'react-device-detect';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { clearAllDummys } from 'store/store';
import { AxiosCancelToken, axios, dataFetcher, endpoints, baseUrl } from 'Api';
import { needsPaymentMethod } from 'Utils/paymentGate';
import { isOtpVerificationTestException } from 'Utils/otpTestExceptions';
import { logAppEvent } from 'Api/errorLogger';
import { createRecaptchaVerifier, sendFirebaseOtp, mapFirebaseAuthError } from 'Api/firebaseAuthClient';
import RotatingLoader from 'Components/RotatingLoader';
import LightRays from 'Components/reactbits/LightRays';
import projectorLogo from 'Assets/Login Logo.png';
import SplitText from 'Components/reactbits/SplitText';
import { motion, AnimatePresence } from 'framer-motion';
import { NAME_MAX_LENGTH, NAME_LIMIT_MESSAGE, nameLimitHandlers } from 'Utils/nameLimit';
import { isNewUIEnabled } from 'hooks/useNewUI';
import Google from '../../../../Assets/google.svg';
import Apple from '../../../../Assets/apple.svg';
import { Input } from '../components/input-field';
import { AuthPage } from '../components/page';
import { StepStatus } from '../components/step-status';
import { getDesktopCallbackUrl } from '../utils/desktopCallback';
import authStyles from '../auth.module.css';
import PasswordCondition from './PasswordCondition';
import GoogleLoginButton from './GoogleLoginButton';
import AppleLogin from './AppleLogin';
import PasswordRequirements from './PasswordRequirements';
import styles from './index.module.css';
import ProjectorAcknowlegingTxt from '../ProjectorAcknowlegingTxt';
import { SignInClassic } from './SignInClassic';

const transitionTimeout = 300;

const SignInNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { refferedBy } = useParams();
  const {
    setAppSnackbar,
    setinvalidErr,
    invalidErr,
    setIsInvalid,
    userDetails,
    setUserDetails,
    plansPage,
    setPlansPage,
    langText,
    setReferralStatus,
    fetchData,
    isInvalid,
    referralCode,
    setFromLogin,
    setLoginWithSocial,
    allPlans
  } = useContext(AppContext);

  const showNameLimitError = () => {
    setAppSnackbar({ isVisible: true, type: 'error', message: NAME_LIMIT_MESSAGE });
  };
  const {
    forceAutoSignin,
    setToken,
    preferredEmail,
    setPreferredEmail,
    preferredUserDetails,
    setPreferredUserDetails,
    setEmailVerified,
  } = useContext(AuthContext);
  const [email, setEmail] = useState(
    isValidEmail(preferredEmail) ? preferredEmail : ''
  );

  const searchParams = new URLSearchParams(location.search);

  const appType = searchParams.get('app');
  const redirectUri = searchParams.get('redirect_uri');
  const isDesktopApp = appType === 'desktop' || appType === 'windows-desktop';
  const isWindowsDesktop = appType === 'windows-desktop' || (appType === 'desktop' && redirectUri);

  // Landing on the sign-in page implies the user is leaving any previous
  // session — drop the manager-impersonation markers so they don't survive
  // into the next login on this browser. Empty deps → runs once on mount.
  useEffect(() => {
    localStorage.removeItem('isAccessingUser');
    localStorage.removeItem('accessedUserId');
    // Starting a fresh sign-up: clear the one-time "Tell your story" prompt flag
    // so a stale value left over from a previous gift flow doesn't suppress the
    // recorder for this new user.
    localStorage.removeItem('giftReceiverRecordPromptAsked');
  }, []);

  // Auto logout and clear storage when callback from desktop app
  useEffect(() => {
    console.log('Desktop App Mode:', { appType, isDesktopApp, isWindowsDesktop, redirectUri });
    if (isDesktopApp) {
      console.log('Desktop app detected, clearing authentication data...');

      // Clear all authentication data immediately
      const authKeys = ['token', 'inviteUser', 'dismissedPaymentDialog', 'isVerified', 'id', 'authToken', 'user', 'userDetails', 'accessToken', 'refreshToken'];
      authKeys.forEach((key) => localStorage.removeItem(key));

      // Clear any additional localStorage items that might contain auth data
      Object.keys(localStorage).forEach((key) => {
        if (key.includes('auth') || key.includes('token') || key.includes('user') || key.includes('session')) {
          localStorage.removeItem(key);
        }
      });

      // Clear all sessionStorage except what we need for desktop app
      sessionStorage.clear();

      // Store desktop app context
      sessionStorage.setItem('isDesktopApp', 'true');
      sessionStorage.setItem('isWindowsDesktop', isWindowsDesktop ? 'true' : 'false');

      // Store redirect URI if provided
      if (redirectUri) {
        sessionStorage.setItem('desktopRedirectUri', redirectUri);
      }

      console.log('Authentication data cleared for desktop app');

      // Force redirect to ensure clean URL and state in production
      const currentUrl = new URL(window.location.href);
      const expectedPath = '/newSign-in';
      const expectedParams = new URLSearchParams({ app: appType });
      if (redirectUri) {
        expectedParams.set('redirect_uri', redirectUri);
      }

      if (currentUrl.pathname !== expectedPath || currentUrl.search !== `?${expectedParams.toString()}`) {
        console.log('Redirecting to clean desktop app URL...');
        window.location.replace(`${window.location.origin}${expectedPath}?${expectedParams.toString()}`);
      }
    }
  }, [isDesktopApp, isWindowsDesktop, appType, redirectUri]);
  const signInText = langText?.signInUp;
  const [password, setPassword] = useState('');
  const [passwordNew, setPasswordNew] = useState('');
  const [emailIsFixed, fixEmail] = useState(false);
  const [emailIsValid, setEmailValidity] = useState(false);
  const [recaptcha, setRecaptcha] = useState('');
  const [cloudflareToken, setCloudflareToken] = useState('');
  const [cloudflareVerifying, setCloudflareVerifying] = useState(true);
  // True once the account has been created (signUp succeeded). After this, a
  // phone-save (updateMobile) failure should let the user correct the number
  // and retry WITHOUT re-running signUp — which would fail ("email exists") and
  // needs a fresh single-use Turnstile token. So the retry just re-fires
  // updateMobile, and the button no longer requires cloudflareToken.
  const [signupCompleted, setSignupCompleted] = useState(false);
  const turnstileRef = React.useRef(null);
  const turnstileWidgetId = React.useRef(null);
  // The email step and the name/phone step each render their own Turnstile
  // container, but only one is mounted at a time. A plain shared ref can be
  // left pointing at the unmounted (detached) email-step node after the
  // transition, so a silent re-mint after a backend error would render the new
  // widget into a node that's no longer in the DOM — Cloudflare never fires the
  // callback and the token (and the disabled "Agree and Continue" button) is
  // stuck. This guarded callback ref keeps turnstileRef pointing at whichever
  // container is currently mounted (it ignores the null on unmount).
  const setTurnstileNode = React.useCallback((node) => {
    if (node) turnstileRef.current = node;
  }, []);
  const [accountExists, setAccountExistence] = useState(undefined);
  const [checkingAccountExistence, setAccountExistenceCheckingStatus] = useState(false);
  // True when the /signinEmail existence probe fails at the transport layer
  // (e.g. Axios "Network Error"). We must NOT treat that as "no account" and
  // silently route the user into sign-up — it may be an existing user on a
  // flaky connection. Instead we surface a retry-able error on Continue.
  const [existenceCheckFailed, setExistenceCheckFailed] = useState(false);
  const [cancelToken, setCancelToken] = useState(undefined);
  const [termsAreAgreed, setTermsAgreement] = useState(false);
  const [selectedReCAPTCHA, selectReCAPTCHA] = useState(false);
  const [loginLoader, setloginLoader] = useState(false);
  const [continueToPassword, setContinueToPassword] = useState(false);
  const [noPassWord, setNoPassWord] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState();
  const passwordSchema = new PasswordValidator()
    .is()
    .min(12)
    .has()
    .symbols(1)
    .has()
    .digits(1)
    .has()
    .uppercase(1)
    .lowercase(1);
  const hasUpperCase = /[A-Z]/.test(password);
  const allPasswordConditionsMet = password
    && password.length >= 12
    && password.length <= 30
    && hasUpperCase
    && password.match(/[a-z]/)
    && password.match(/[0-9]/)
    && password.match(/[!@#$%^&*()_+|~=`{}[\]:";'<>?,./]/);
  const passwordHealth = password
    ? password.length < 12 || !hasUpperCase
      ? 'weak'
      : passwordSchema.validate(password)
        ? password.length >= 12
          ? 'strong'
          : 'medium'
        : 'weak'
    : '';
  const labelText = pathname.includes('/newSign-in')
    ? 'Login to Projector'
    : pathname.includes('/newSign-up')
      ? 'Create Your Projector'
      : '';
  // Supporting line under the heading — presentational copy only, mirrors the
  // same two routes labelText covers.
  const subLabelText = pathname.includes('/newSign-in')
    ? 'Welcome back. Sign in to continue to your Projector.'
    : pathname.includes('/newSign-up')
      ? 'Create your account and start building your Projector.'
      : '';

  const dispatch = useDispatch();
  const clearAllDummy = () => {
    dispatch(clearAllDummys());
  };
  const reCaptchaHandler = () => {
    window.grecaptcha
      .execute(GOOGLE_RECAPTCHA_KEY, { action: 'submit' })
      .then((value) => {
        if (value) {
          setRecaptcha(value);
          selectReCAPTCHA(true);
        } else {
          selectReCAPTCHA(false);
        }
      });
  };

  // Cloudflare Turnstile tokens are single-use: once the backend consumes one
  // (e.g. on sign-up) it is burned. resetTurnstile tears the widget down so a
  // fresh one can be rendered.
  const resetTurnstile = () => {
    if (window.turnstile && turnstileRef.current) {
      if (turnstileWidgetId.current) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (e) {
          // widget may already be gone — ignore
        }
        turnstileWidgetId.current = null;
      }
      turnstileRef.current.innerHTML = '';
      setCloudflareToken('');
      setCloudflareVerifying(true);
    }
  };

  const renderTurnstile = () => {
    if (window.turnstile && turnstileRef.current) {
      try {
        // Check if already rendered to avoid duplicate widgets
        if (!turnstileRef.current.hasChildNodes()) {
          setCloudflareVerifying(true);
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: CLOUDFLARE_TURNSTILE_SITE_KEY,
            callback: (token) => {
              setCloudflareToken(token);
              setCloudflareVerifying(false);
            },
            'expired-callback': () => {
              setCloudflareToken('');
              setCloudflareVerifying(true);
              if (window.turnstile && turnstileWidgetId.current) {
                window.turnstile.reset(turnstileWidgetId.current);
              }
            },
            'error-callback': () => {
              setCloudflareVerifying(false);
            },
            theme: 'light',
          });
        }
      } catch (error) {
        setCloudflareVerifying(false);
      }
    }
  };

  // Called from every API error/non-success handler so the consumed token is
  // silently swapped for a fresh one — the user never has to refresh the page.
  const refreshTurnstile = () => {
    resetTurnstile();
    setTimeout(renderTurnstile, 100);
  };

  // Post-login verification gate. getMyProfile is the source of truth here —
  // the signin response can be stale.
  //
  // Accounts on the testing allowlist (Utils/otpTestExceptions) are never held.
  //
  // Held back for OTP verification when BOTH are true:
  //   - `password` is set (a real email/password account, as opposed to a
  //     social login, which has no password and isn't gated on the mobile)
  //   - `verify_mobile` is not 1
  // `is_email_verified` is deliberately not part of this.
  //
  // Absent fields fail open: no `verify_mobile` or no `password` in the
  // response means we can't judge, so the user is let through rather than
  // held — a response-shape change must never lock everyone out.
  const checkVerificationAfterLogin = async (loginEmail) => {
    try {
      const profile = await dataFetcher(endpoints.getMyProfile);
      const profileData = profile?.data;
      if (!profile?.success || !profileData) return false;

      if (profileData.verify_mobile === undefined) return false;
      const mobileVerified = parseInt(profileData.verify_mobile, 10) === 1;
      if (mobileVerified) return false;

      // TESTING ONLY — allowlisted accounts skip the gate regardless of
      // verify_mobile. See Utils/otpTestExceptions.
      if (isOtpVerificationTestException(profileData.email || loginEmail)) return false;

      const passwordIsSet = Boolean(profileData.password)
        && String(profileData.password).trim() !== '';
      if (!passwordIsSet) return false;

      // The OTP screen reads both of these out of session storage.
      sessionStorage.setItem(
        encryptedKeyForCredentials.email,
        profileData.email || loginEmail
      );
      if (profileData.mobile) {
        sessionStorage.setItem(encryptedKeyForCredentials.mobile, profileData.mobile);
      }
      sessionStorage.removeItem('otpFlowCompletedPath');

      // `replace` keeps the login screen out of the history stack; the OTP
      // screen locks Back on top of that. With no number on file we have to
      // collect one before a code can go anywhere.
      navigate(
        profileData.mobile
          ? Pathname.authentication.newverifyCode
          : Pathname.authentication.newmobileVerification,
        { replace: true }
      );
      return true;
    } catch (error) {
      // Never strand a valid login on a profile-fetch failure — let it through
      // and rely on the next gate.
      return false;
    }
  };

  // Stores the token for the account that was just created, and nothing else —
  // no navigation, no forceAutoSignin (which would flip the app into its
  // signed-in route tree and pull the user off this screen mid-flow). The token
  // alone is what lets the app-load gate recognise a returning unverified user.
  // Failure is non-fatal: verification still completes, it just can't be
  // restored if the browser is closed.
  const storeSessionAfterSignup = async () => {
    try {
      const res = await dataFetcher(endpoints.signin, { email, password });
      if (res?.data?.token) {
        setToken(res.data.token);
      }
      if (res?.api_response?.access) {
        localStorage.setItem('uploader_token', res.api_response.access);
        try {
          const payload = JSON.parse(atob(res.api_response.access.split('.')[1]));
          if (payload.exp) {
            localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
          }
        } catch (e) {
          // token still usable — the server is the authority on expiry
        }
      }
      if (res?.api_response?.uuid) {
        localStorage.setItem('user_uuid', res.api_response.uuid);
      }
    } catch (error) {
      // Swallowed on purpose — see above.
    }
  };

  /**
   * An account with no card on file has to add one before it can use the app
   * (Lifetime plans excepted — see Utils/paymentGate). Deciding that HERE, off
   * the subscription call we make ourselves, means a gated user goes from the
   * login screen straight to /quick-user-payment. The app-level gate would get
   * there too, but only after the dashboard had already painted.
   *
   * Returns the path to send the user to, or null to carry on as normal — and
   * null on any failure, so a hiccup in this call can never block a login.
   */
  const resolveGatedLoginPath = async () => {
    try {
      const subscription = await dataFetcher(endpoints.getMySubscription);
      return needsPaymentMethod(subscription, allPlans)
        ? Pathname.authentication.quickUserPayment
        : null;
    } catch (e) {
      return null;
    }
  };

  const { isValidating: signingInUser, mutate: signInUser } = useSWR(
    [endpoints.signin, email, password, referralCode],
    {
      fetcher: (url, inputEmail, inputPassword, referralCode) => dataFetcher(url, {
        email: inputEmail,
        password: inputPassword,
        affliateCode: referralCode,
      }),
      onSuccess: ({
        isNewUser,
        success,
        data,
        has_logged_in,
        referral_status,
        is_skip,
        message,
        verify_mobile,
        mobile,
        api_response
      }) => {
        if (success) {
          // if (parseInt(data?.is_email_verified, 10) === 0) {
          //   setAppSnackbar({ type: 'error', message: 'Email verification is pending', isVisible: true });
          // } else {
          setUserDetails({ ...userDetails, isNewUser });
          setToken(data?.token);
          setEmailVerified(data?.is_email_verified);

          // Save upload token and UUID for secure upload system
          if (api_response?.access) {
            localStorage.setItem('uploader_token', api_response.access);
            // Parse expiry from JWT
            try {
              const payload = JSON.parse(atob(api_response.access.split('.')[1]));
              if (payload.exp) {
                localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
              }
            } catch (e) {
              console.warn('Could not parse upload token expiry');
            }
          }
          if (api_response?.uuid) {
            localStorage.setItem('user_uuid', api_response.uuid);
          }

          const rawName = (data?.firstname || data?.first_name || '').trim()
            || (email ? email.split('@')[0].replace(/[._-]+/g, ' ').trim() : '');
          const welcomeName = rawName
            ? rawName.charAt(0).toUpperCase() + rawName.slice(1)
            : '';
          setAppSnackbar({
            message: welcomeName
              ? `Welcome, ${welcomeName}. Ready to watch new content added by your projectors?`
              : 'Welcome. Ready to watch new content added by your projectors?',
            type: 'success',
            isVisible: true,
          });
          fetchData();

          // Handle desktop app callback for successful login
          if (isDesktopApp) {
            const callbackParams = {
              token: api_response?.access || '',
              projector_token: data?.token || '',
              email,
              isNewUser: isNewUser || 'false'
            };
            window.location.href = getDesktopCallbackUrl(callbackParams);
            return;
          }

          // Hold unverified users on the OTP screen instead of routing them
          // into the app; checkVerificationAfterLogin has already navigated
          // there when it resolves true.
          checkVerificationAfterLogin(email).then((heldForVerification) => {
            if (heldForVerification) return;
            setTimeout(async () => {
              if (
                parseInt(referral_status, 10) === 1
                && parseInt(is_skip, 10) !== 1
                && !data?.billing_date
              ) {
                setPlansPage(false);
                parseInt(referral_status, 10) === 1 && parseInt(is_skip, 10) !== 1
                  ? setReferralStatus(1)
                  : setReferralStatus(0);
                //  navigate(Pathname.plans);
              } else {
                // No card on file → straight to the payment page, skipping
                // /access entirely, so the dashboard never gets a frame.
                const gatedPath = await resolveGatedLoginPath();
                if (gatedPath) {
                  localStorage.removeItem('storedPath');
                  navigate(gatedPath, { replace: true });
                  return;
                }
                // has_logged_in === '0' ? navigate(Pathname.gettingStarted) : navigate(Pathname.access);
                const storedPath = localStorage.getItem('storedPath');
                if (storedPath) {
                  localStorage.removeItem('storedPath');
                  // Hard navigation, NOT navigate(). forceAutoSignin() above
                  // unmounts the whole <Router> (app.jsx swaps it for the
                  // "Getting your Projector ready" spinner), so this SPA
                  // navigate would run against the old, dead router: the
                  // address bar changes but the freshly mounted router keeps
                  // rendering whatever it resolved on mount (/access). A full
                  // load lands on the stored path for real.
                  window.location.replace(storedPath);
                } else {
                  navigate(
                    `${Pathname.access}${data?.encVal ? `?email=${data?.encVal}` : ''
                    }`
                  );
                }
              }
            }, 500);
          });
          forceAutoSignin();
          // }
          setFromLogin(true); // this is only for showing add card popup
        } else if (!verify_mobile) {
          // Handle desktop app callback for mobile verification needed
          if (isDesktopApp) {
            const callbackParams = {
              token: api_response?.access || '',
              projector_token: data?.token || '',
              email,
              isNewUser
            };
            window.location.href = getDesktopCallbackUrl(callbackParams);
            return;
          }

          if (mobile) {
            navigate(Pathname.authentication.newmobileVerification);
            sessionStorage.setItem(encryptedKeyForCredentials.mobile, mobile);
          } else {
            navigate(Pathname.authentication.newmobileVerification);
          }
        } else {
          setAppSnackbar({
            type: 'error',
            message: message || 'Incorrect password',
            isVisible: true,
          });
        }
      },
      onError: (error) => {
        const responseData = error?.response?.data;

        logAppEvent({
          userName: email || 'Guest',
          functionAffected: 'Sign In',
          description: `Sign in failed for email: ${email || 'unknown'} — ${responseData?.message || error?.message || 'Unknown error'}`,
          severity: 'high',
          errorMessage: responseData?.message || error?.message || 'Sign in failed',
          errorStack: error?.stack || null,
          responseStatus: error?.response?.status || null,
          responseBody: JSON.stringify(responseData) || null,
          requestUrl: endpoints.signin,
          requestMethod: 'POST',
          component: 'NewSignIn',
          userAction: 'sign in attempt',
        });

        // Handle desktop app callback for login errors
        if (isDesktopApp) {
          window.location.href = getDesktopCallbackUrl(responseData?.message || 'Login failed', true);
          return;
        }

        // if ('email_verified' in responseData && !responseData?.email_verified) {
        //   sessionStorage.setItem('emailForValidation_projectoruser', email);
        //   navigate(Pathname.authentication.verifyEmail);
        // } else {
        // setAppSnackbar({ type: 'error', message: responseData?.message || 'Oops! Something went wrong', isVisible: true });
        setIsInvalid(true);
        setinvalidErr({
          message: responseData?.message || 'Oops! Something went wrong',
        });
        // }
      },
    }
  );
  // Requests the OTP to be sent to the just-saved mobile number, then jumps
  // straight to the code-entry screen. Mirrors the standalone phone screen so
  // the verification step behaves identically — Firebase Phone Auth sends and
  // checks the SMS code directly from the browser, bypassing the old
  // Twilio-backed sendOtpToMobile endpoint entirely.
  const [requestingCode, setRequestingCode] = useState(false);
  const otpRecaptchaVerifierRef = useRef(null);
  const getOtpRecaptchaVerifier = () => {
    if (!otpRecaptchaVerifierRef.current) {
      otpRecaptchaVerifierRef.current = createRecaptchaVerifier('firebase-recaptcha-container');
    }
    return otpRecaptchaVerifierRef.current;
  };
  const requestVerificationCode = async () => {
    setRequestingCode(true);
    try {
      const verifier = getOtpRecaptchaVerifier();
      await sendFirebaseOtp(phoneNumber, verifier);
      sessionStorage.setItem('#fs656gfhllh@#$', true);
      sessionStorage.removeItem('otpFlowCompletedPath');
      setAppSnackbar({ message: 'Code sent to your mobile number', isVisible: true });
      // Registration is done — `replace` drops the signup screen from the
      // history stack so Back can't return to it (the OTP screen locks Back
      // on top of that).
      navigate(Pathname.authentication.newverifyCode, { replace: true });
    } catch (error) {
      setAppSnackbar({ message: mapFirebaseAuthError(error), isVisible: true });
      otpRecaptchaVerifierRef.current = null;
      reCaptchaHandler();
      refreshTurnstile();
    } finally {
      setRequestingCode(false);
    }
  };

  // Saves the phone number entered on the sign-up screen against the new
  // account, then triggers the OTP request above.
  const { isValidating: updatingMobile, mutate: updateMobile } = useSWR(
    [endpoints.updateMyProfile, phoneNumber, email],
    {
      fetcher: (url, inputMobile, inputEmail) => dataFetcher(url, { mobile: inputMobile, email: inputEmail }),
      onSuccess: ({ success, message, token }) => {
        if (success) {
          setUserDetails({ ...userDetails, mobile: phoneNumber });
          sessionStorage.setItem('mvt', token);
          requestVerificationCode();
        } else {
          setAppSnackbar({
            isVisible: true,
            message:
              message
              || 'Oops! Something went wrong while updating phone number',
          });
          // Silently re-mint the (now single-use-burned) Cloudflare token so the
          // user can correct the number and retry without refreshing the page.
          refreshTurnstile();
        }
      },
      onError: (error) => {
        const responseData = error?.response?.data;
        setAppSnackbar({ isVisible: true, message: responseData?.message || 'Oops! Something went wrong' });
        refreshTurnstile();
      },
    }
  );

  const { isValidating: signingUpUser, mutate: signUpUser } = useSWR(
    [
      endpoints.signup,
      email,
      password,
      refferedBy,
      preferredUserDetails.firstName,
      preferredUserDetails.lastName,
      cloudflareToken,
      searchParams.get('reference_code'),
      searchParams.get('referral'),
    ],
    {
      fetcher: (
        url,
        inputEmail,
        inputPassword,
        refferedLink,
        firstname,
        lastname,
        cloudflareToken,
        referenceCode,
        referralKey
      ) => dataFetcher(url, {
        email: inputEmail,
        password: inputPassword,
        refferedBy: refferedLink || null,
        firstname,
        lastname,
        recaptchaResponse: cloudflareToken,
        affiliateCode: referenceCode || null,
        referral_key: referralKey || null,
      }),
      onSuccess: ({ success }) => {
        if (success) {
          // sessionStorage.setItem('emailForValidation_projectoruser', email);
          // sessionStorage.setItem('password_projectoruser', password);
          sessionStorage.setItem(encryptedKeyForCredentials.password, password);
          sessionStorage.setItem(encryptedKeyForCredentials.email, email);
          sessionStorage.setItem(encryptedKeyForCredentials.mobile, phoneNumber);
          setPlansPage(false);
          // Account is now created — a later phone failure must not re-run signUp.
          setSignupCompleted(true);
          // Sign in straight away, purely to obtain and store the token. Without
          // a session, a user who closes the browser mid-verification comes back
          // as an anonymous visitor and the app-load gate has no profile to
          // check — so they could reach the site without being sent back to the
          // OTP screen. Deliberately NOT signInUser(): that one routes the user
          // into the app on success, abandoning this flow.
          storeSessionAfterSignup();
          // The phone number is now collected on this screen, so save it to the
          // new account and request the OTP right away, then land directly on
          // the code-entry screen — skipping the standalone phone-number page.
          updateMobile();
        } else {
          // Sign-up consumed the Cloudflare token even though it didn't succeed —
          // re-mint silently so a retry isn't blocked.
          refreshTurnstile();
        }
      },
      onError: (error) => {
        logAppEvent({
          userName: email || 'Guest',
          functionAffected: 'Sign Up',
          description: `Sign up failed for email: ${email || 'unknown'} — ${error?.response?.data?.message || error?.message || 'Unknown error'}`,
          severity: 'high',
          errorMessage: error?.response?.data?.message || error?.message || 'Sign up failed',
          errorStack: error?.stack || null,
          responseStatus: error?.response?.status || null,
          responseBody: JSON.stringify(error?.response?.data) || null,
          requestUrl: endpoints.signup,
          requestMethod: 'POST',
          component: 'NewSignIn',
          userAction: 'sign up attempt',
        });
        // The token is single-use and already burned by the backend — re-mint it
        // silently so the user can retry without refreshing the page.
        refreshTurnstile();
      },
    }
  );
  const { isValidating: gettingMFAStatus, mutate: getMFAStatus } = useSWR(
    [endpoints.getMFAStatus, email, password],
    {
      fetcher: (url, inputEmail) => dataFetcher(url, { email: inputEmail }),
      onSuccess: ({ verify_mobile, success, phone_no, mfa, message }) => {
        if (success) {
          if (verify_mobile && mfa) {
            navigate(Pathname.authentication.newmobileVerification);
          } else {
            signInUser();
          }
        } else {
          setAppSnackbar({
            type: 'error',
            message: message || 'Incorrect password',
            isVisible: true,
          });
        }
      },
      onError: (error) => {
        const responseData = error?.response?.data;

        logAppEvent({
          userName: email || 'Guest',
          functionAffected: 'MFA Status Check',
          description: `MFA status check failed for email: ${email || 'unknown'} — ${responseData?.message || error?.message || 'Unknown error'}`,
          severity: 'medium',
          errorMessage: responseData?.message || error?.message || 'MFA check failed',
          errorStack: error?.stack || null,
          responseStatus: error?.response?.status || null,
          responseBody: JSON.stringify(responseData) || null,
          requestUrl: endpoints.getMFAStatus,
          requestMethod: 'POST',
          component: 'NewSignIn',
          userAction: 'MFA status check during login',
        });

        setAppSnackbar({
          type: 'error',
          message: responseData?.message || 'Oops! Something went wrong',
          isVisible: true,
        });
      },
    }
  );

  useEffect(() => {
    const loadScriptByURL = (id, url, callback) => {
      const isScriptExist = document.getElementById(id);
      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = function onScriptLoad() {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }
      if (isScriptExist && callback) callback();
    };
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${GOOGLE_RECAPTCHA_KEY}`,
      () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            reCaptchaHandler();
          });
        }
      }
    );
    // Load Cloudflare Turnstile script
    loadScriptByURL(
      'turnstile-script',
      'https://challenges.cloudflare.com/turnstile/v0/api.js',
      () => {
        console.log('Turnstile script loaded');
        // Attempt to render after script loads
        setTimeout(() => {
          renderTurnstile();
        }, 100);
      }
    );
  }, []);

  const [isThereAnyAccount, setIsThereAnyAccount] = useState(undefined);
  const { isValidating: checkingDB, mutate: checkAccountExistence } = useSWR(
    // Null key until an email is present, so SWR doesn't fire a spurious existence
    // probe with email:'' when the cancel token settles on mount.
    email ? [endpoints.signinEmail, email, cancelToken?.token] : null,
    {
      fetcher: (url, inputEmail, uniqueCancelToken) => dataFetcher(url, { email: inputEmail }, uniqueCancelToken),
      onSuccess: ({ success, no_password }) => {
        setIsThereAnyAccount(success);
        setNoPassWord(no_password);
        setAccountExistenceCheckingStatus(false);
        setExistenceCheckFailed(false);
        if (success) {
          setAccountExistence(false);

          if (isValidEmail(preferredEmail)) {
            fixEmail(true);
          }
        }
        // Don't auto-redirect to sign-up form - let user click Continue button
        // setAccountExistence(true) is now only set when user clicks Continue

        setPreferredEmail(undefined);
      },
      onError: (error) => {
        logAppEvent({
          userName: email || 'Guest',
          functionAffected: 'Check Account Existence',
          description: `Account existence check failed for email: ${email || 'unknown'} — ${error?.response?.data?.message || error?.message || 'Unknown error'}`,
          severity: 'medium',
          errorMessage: error?.response?.data?.message || error?.message || 'Account check failed',
          errorStack: error?.stack || null,
          responseStatus: error?.response?.status || null,
          responseBody: JSON.stringify(error?.response?.data) || null,
          requestUrl: endpoints.signinEmail,
          requestMethod: 'POST',
          component: 'NewSignIn',
          userAction: 'email existence check during login',
        });
        setAccountExistenceCheckingStatus(false);
        // The probe never resolved, so we don't know if the account exists.
        // Keep isThereAnyAccount undefined and flag the failure so Continue
        // retries instead of assuming the user is new.
        setIsThereAnyAccount(undefined);
        setExistenceCheckFailed(true);
      },
    }
  );

  const emailInputHandler = debounce(
    ({ target: { value: inputEmail } }) => {
      // Editing the email starts a fresh account creation again.
      setSignupCompleted(false);
      setExistenceCheckFailed(false);
      if (isValidEmail(inputEmail)) {
        setEmail(inputEmail);
        setEmailValidity(true);
      } else {
        setEmail('');
        setAccountExistence(undefined);
        setEmailValidity(false);
      }
    },
    750,
    { trailing: true }
  );

  const inputHandlerForUserDetails = (key, value) => {
    const userDetailsCopy = { ...preferredUserDetails };
    userDetailsCopy[key] = value;
    setPreferredUserDetails(userDetailsCopy);
  };

  useEffect(() => {
    if (email) {
      setAccountExistenceCheckingStatus(true);
      checkAccountExistence();
    }
  }, [email, setAccountExistenceCheckingStatus, checkAccountExistence]);

  useEffect(() => {
    if (preferredEmail && isValidEmail(preferredEmail)) {
      // NOTE: do NOT set checkingAccountExistence here. The actual
      // checkAccountExistence() call below is intentionally commented out, so
      // nothing would ever reset the flag back to false — it would stay true
      // and permanently disable the "Agree and Continue" button (the flag is
      // one of its disabled terms). The submit onClick sets preferredEmail,
      // which is what was firing this effect and bricking the retry.
      setEmailValidity(true);
      // checkAccountExistence();
    }
  }, [preferredEmail, checkAccountExistence]);

  useEffect(() => {
    // Render Turnstile on the screens that consume a token: the initial email
    // screen and the new-user sign-up screen. Re-running when the screen
    // changes also re-mints a fresh token after a back/reset.
    if ((!accountExists && !continueToPassword) || accountExists === true) {
      // Delay rendering to ensure the DOM element is available
      const timer = setTimeout(() => {
        renderTurnstile();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [accountExists, continueToPassword]);

  const saveEmailAndPasswordToSession = () => {
    sessionStorage.setItem(encryptedKeyForCredentials.password, password);
    sessionStorage.setItem(encryptedKeyForCredentials.email, email);
  };
  const isNameFilled = preferredUserDetails.firstName.trim() !== ''
    && preferredUserDetails.lastName.trim() !== '';

  const handlePasswordInput = (event) => {
    const inputPassword = event.target.value;
    if (inputPassword.length > 30) {
      // alert('Password must be 30 characters or less.');
    } else {
      setPassword(inputPassword);
    }
  };
  const getCode = () => {
    sessionStorage.setItem(encryptedKeyForCredentials.email, email);
    navigate(Pathname.authentication.newverifyCodeEmail);
  };
  const loginWithSocial = async (tokenResponse, type) => {
    setloginLoader(true);
    try {
      const affiliateCode = searchParams.get('reference_code');
      const referralKey = searchParams.get('referral');
      const payload = type === 'Apple'
        ? { apple_token: tokenResponse, affiliateCode: affiliateCode || null, referral_key: referralKey || null }
        : { google_token: tokenResponse.access_token, affiliateCode: affiliateCode || null, referral_key: referralKey || null };
      const response = await axios.post(
        `${baseUrl}/auth/signin-social`,
        payload
      );
      const res = response.data;
      if (res.new_user) {
        setPreferredEmail(res?.email);
        sessionStorage.setItem(encryptedKeyForCredentials.email, res?.email);
        setPlansPage(false);
        setToken(res?.data?.token);
        forceAutoSignin();

        // Handle desktop app callback for new users
        if (isDesktopApp) {
          const callbackParams = {
            token: res?.api_response?.access || '',
            projector_token: res.data?.token || '',
            email: res?.email || '',
            isNewUser: res.new_user
          };
          window.location.href = getDesktopCallbackUrl(callbackParams);
          return res;
        }

        navigate(Pathname.authentication.avatar);
        if (window.gtag) {
          window.gtag('event', 'sign_up', {
            method: type === 'Apple' ? 'Apple' : 'Google', // or 'google', etc., based on your signup method
            // user_id: data?.id,
            email: res?.email,
            timestamp: new Date().toISOString(), // GA will treat this as a custom param
          });
        }
      } else if (res.success) {
        const { data, api_response, isNewUser } = res;

        setUserDetails((prev) => ({
          ...prev,
          ...data,
          isNewUser: false,
        }));

        setToken(data?.token);
        forceAutoSignin();
        setEmailVerified(data?.is_email_verified);

        setAppSnackbar({
          message: res.message || 'Logged in successfully',
          isVisible: true,
        });

        fetchData();

        // Handle desktop app callback for successful login
        if (isDesktopApp) {
          const callbackParams = {
            token: api_response?.access || '',
            projector_token: data?.token || '',
            email: data?.email || res?.email || '',
            isNewUser: res.new_user
          };
          window.location.href = getDesktopCallbackUrl(callbackParams);
          return res;
        }

        // Same verification gate as the password login above.
        const heldForVerification = await checkVerificationAfterLogin(
          data?.email || res?.email || ''
        );
        if (heldForVerification) {
          setloginLoader(false);
          return res;
        }

        setTimeout(async () => {
          const hasReferral = parseInt(res.referral_status, 10) === 1;
          const isNotSkipped = parseInt(res.is_skip, 10) !== 1;

          if (hasReferral && isNotSkipped && !data?.billing_date) {
            setPlansPage(false);
            setReferralStatus(1);
            // navigate(Pathname.plans);
          } else {
            // Same no-card check the password login does — social sign-in
            // lands on the payment page directly too.
            const gatedPath = await resolveGatedLoginPath();
            if (gatedPath) {
              localStorage.removeItem('storedPath');
              navigate(gatedPath, { replace: true });
              return;
            }
            const storedPath = localStorage.getItem('storedPath');
            if (storedPath) {
              localStorage.removeItem('storedPath');
              // Full load, not navigate() — see the note above: the router is
              // being torn down by forceAutoSignin() at this point.
              window.location.replace(storedPath);
            } else {
              navigate(
                `${Pathname.authentication.access}${data?.encVal ? `?email=${data.encVal}` : ''
                }`
              );
            }
          }
        }, 500);
        setFromLogin(true); // to trigger add card popup
      } else if (res.data?.verify_mobile !== '1') {
        const mobile = res.data?.mobile;
        navigate(Pathname.authentication.access);
        if (mobile) {
          sessionStorage.setItem(encryptedKeyForCredentials.mobile, mobile);
        }
      } else {
        setAppSnackbar({
          type: 'error',
          message: res.message || 'Incorrect password',
          isVisible: true,
        });
      }
      setloginLoader(false);
      return res;
    } catch (error) {
      console.error('Login failed:', error.response?.data || error.message);

      logAppEvent({
        userName: email || 'Guest',
        functionAffected: 'Social Login',
        description: `Social login failed — ${error.response?.data?.message || error.message || 'Unknown error'}`,
        severity: 'high',
        errorMessage: error.response?.data?.message || error.message || 'Social login failed',
        errorStack: error?.stack || null,
        responseStatus: error?.response?.status || null,
        responseBody: JSON.stringify(error?.response?.data) || null,
        requestUrl: `${baseUrl}/auth/signin-social`,
        requestMethod: 'POST',
        component: 'NewSignIn',
        userAction: 'social login attempt (Google/Apple)',
      });

      // Handle desktop app callback for errors
      if (isDesktopApp) {
        window.location.href = getDesktopCallbackUrl(error.response?.data?.message || error.message || 'Social login failed', true);
        setloginLoader(false);
        return;
      }

      setAppSnackbar({
        type: 'error',
        message:
          error.response?.data.message
          || error.message
          || 'Something went wrong during login',
        isVisible: true,
      });
      setloginLoader(false);
      throw error;
    }
  };
  const handleBackOrReset = () => {
    if (!accountExists && !noPassWord && !continueToPassword) {
      // navigate(-1)
      window.location.href = 'https://www.projectorstream.com/';
    } else {
      setAccountExistence(false);
      setNoPassWord(false);
      setContinueToPassword(false);
      setPassword('');
      // setEmail('');
      setPreferredUserDetails({
        firstName: '',
        lastName: '',
      });
    }
  };
  console.log('checkingDB', checkingDB);

  return (
    <AuthPage
      disableRedirect={!isMobile}
      hideLogo
      className={styles.loginPage}
      contentClassName={styles.loginContent}
    >
      {/* Backdrop: near-black with volumetric light rays fanning down from the
          top, so the card sits inside the beam. */}
      <div className={styles.raysLayer} aria-hidden="true">
        <LightRays
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={0.9}
          lightSpread={0.85}
          rayLength={1.25}
          fadeDistance={1.1}
          saturation={1}
          followMouse
          mouseInfluence={0.08}
          noiseAmount={0.08}
          distortion={0.04}
        />
      </div>
      {loginLoader && <RotatingLoader dark />}
      {!isMobile && !isDesktopApp && (
        <div
          aria-hidden="true"
          onClick={handleBackOrReset}
          className={styles.backLink}
        >
          {signInText?.back || 'BACK'}
        </div>
      )}
      <div className={styles.authCard}>
      {!emailIsFixed ? (
        // This wrapper used to be a <form> whose only job was to swallow submits
        // (onSubmit -> preventDefault). But it WRAPPED the password <form> below,
        // and nested forms are invalid HTML: the browser does not propagate the
        // inner form's `submit` event beyond that inner form. React 17+ listens for
        // events on the root container, so React never received the submit, never
        // ran the password form's onSubmit, and never called preventDefault() —
        // the browser then performed its default GET submission to "/newSign-in?",
        // reloading the page and bouncing the user back to the email step instead
        // of logging them in. A plain <div> removes the nesting; the inner forms
        // are now top-level and their onSubmit handlers fire normally.
        <div>
          {!accountExists
            // && !checkingAccountExistence
            && (
              <div className={styles.cardHead}>
                <div className={styles.cardHeadTitleRow}>
                  <SplitText
                    text={labelText.replace(/\s*Projector\s*$/i, '').trim() || labelText}
                    className={styles.cardHeadLoginText}
                  />
                  <img src={projectorLogo} alt="Projector" className={styles.cardHeadLogo} />
                </div>
                {subLabelText && (
                  <p className={styles.cardSubtitle}>{subLabelText}</p>
                )}
              </div>
            )}

          {(accountExists === undefined
            || accountExists === false
            || noPassWord)
            && (
              <div className={styles.stepIn}>
                <AnimatePresence initial={false}>
                  {!noPassWord && !continueToPassword && (
                    <motion.div
                      key="social-options"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className={styles.loginOptions}>
                        <GoogleLoginButton loginWithSocial={loginWithSocial} />
                        <AppleLogin loginWithSocial={loginWithSocial} />
                      </div>
                      <p className={styles.orSignUp}>Continue with</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className={styles.fieldWrap}>
                <Input
                  noLabel={!noPassWord}
                  className={styles.customuserInput}
                  defaultValue={email}
                  isDisabled={
                    continueToPassword
                    || (email === preferredEmail && checkingAccountExistence)
                  }
                  isLoading={checkingAccountExistence}
                  strokeLoader
                  // label="Login to Projector"
                  labelAlignCenter
                  onInput={(e) => {
                    e.persist();
                    emailInputHandler(e);
                    setAccountExistenceCheckingStatus(false);

                    if (cancelToken) {
                      cancelToken.cancel();
                    }

                    if (!e?.target?.value) {
                      setEmail('');
                      setAccountExistence(undefined);
                      setEmailValidity(false);
                      setNoPassWord(false);
                    } else {
                      setCancelToken(AxiosCancelToken.source());
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && email && !checkingDB && cloudflareToken && !cloudflareVerifying) {
                      e.preventDefault();
                      if (noPassWord) {
                        getCode();
                      } else if (isThereAnyAccount) {
                        setContinueToPassword(true);
                      } else if (isThereAnyAccount === false) {
                        setAccountExistence(true);
                      } else {
                        // Existence unknown (probe errored / not yet run) — retry
                        // rather than assuming a new account.
                        setExistenceCheckFailed(false);
                        setAccountExistenceCheckingStatus(true);
                        checkAccountExistence();
                      }
                    }
                  }}
                  placeholder={signInText?.email || 'Email'}
                  type="email"
                />
                </div>
                {existenceCheckFailed && !checkingAccountExistence && (
                  <p className={styles.inlineError}>
                    We couldn&apos;t verify your email. Check your connection and
                    press Continue to try again.
                  </p>
                )}
                <div
                  ref={setTurnstileNode}
                  className={styles.turnstileWrap}
                  style={{ display: continueToPassword ? 'none' : undefined }}
                />
                {!continueToPassword && (noPassWord ? (
                  <Button
                    id="sasdasddd"
                    className={styles.primaryAction}
                    blockText
                    isTopMargin15
                    onClick={getCode}
                    isDisabled={gettingMFAStatus || signingInUser}
                    isFullWidth
                    isLarge
                    submit
                    isBlue
                  >
                    Get Code
                  </Button>
                ) : (
                  <Button
                    id="sasdasddd"
                    className={styles.primaryAction}
                    blockText
                    isTopMargin15
                    onClick={() => {
                      if (!checkingDB && cloudflareToken && !cloudflareVerifying) {
                        if (isThereAnyAccount) {
                          setContinueToPassword(true);
                        } else if (isThereAnyAccount === false) {
                          setAccountExistence(true);
                        } else {
                          // Existence unknown (probe errored / not yet run) —
                          // retry rather than assuming a new account.
                          setExistenceCheckFailed(false);
                          setAccountExistenceCheckingStatus(true);
                          checkAccountExistence();
                        }
                      }
                    }}
                    isDisabled={!email || checkingDB || !cloudflareToken || cloudflareVerifying}
                    isFullWidth
                    isLarge
                    submit
                    isBlue
                  >
                    Continue
                  </Button>
                ))}
              </div>
            )}
          {accountExists === true && (
            <div className={styles.stepIn}>
              {/* <Input
                className={styles.customuserInput}
                defaultValue={email}
                isDisabled={
                  email === preferredEmail && checkingAccountExistence
                }
                isLoading={checkingAccountExistence}
                label={
                  preferredUserDetails.firstName
                  || preferredUserDetails.lastName
                    ? `Hey, ${preferredUserDetails.firstName} ${preferredUserDetails.lastName}`.trim()
                    : 'Create Your Projector'
                }
                labelAlignCenter
                onInput={(e) => {
                  e.persist();
                  emailInputHandler(e);
                  setAccountExistenceCheckingStatus(false);

                  if (cancelToken) {
                    cancelToken.cancel();
                  }

                  if (!e?.target?.value) {
                    setEmail('');
                    setAccountExistence(undefined);
                    setEmailValidity(false);
                    setNoPassWord(false);
                  } else {
                    setCancelToken(AxiosCancelToken.source());
                  }
                }}
                placeholder={signInText?.email || 'Email'}
                type="email"
              /> */}
              <div className={styles.editEMial}>
                <span className={styles.greetingText}>
                  {' '}
                  Hey,
                  {' '}
                  {email}
                </span>
                <svg
                  className={styles.editIcon}
                  onClick={handleBackOrReset}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </div>
              <div className={styles.nameRow}>
                <div className={styles.fieldWrap}>
                  <Input
                    automaticFocus
                    className={styles.customuserInput}
                    onInput={(e) => {
                      e.persist();
                      inputHandlerForUserDetails('firstName', (e?.target?.value || '').slice(0, NAME_MAX_LENGTH));
                    }}
                    placeholder={signInText?.firstName || 'First Name'}
                    type="code"
                    maxLength={NAME_MAX_LENGTH}
                    {...nameLimitHandlers(showNameLimitError)}
                    noLabel
                  />
                </div>
                <div className={styles.fieldWrap}>
                  <Input
                    className={styles.customuserInput}
                    onInput={(e) => {
                      e.persist();
                      inputHandlerForUserDetails('lastName', (e?.target?.value || '').slice(0, NAME_MAX_LENGTH));
                    }}
                    placeholder={signInText?.lastName || 'Last Name'}
                    type="code"
                    maxLength={NAME_MAX_LENGTH}
                    {...nameLimitHandlers(showNameLimitError)}
                    noLabel
                  />
                </div>
              </div>
              <div className={styles.phoneNumberField}>
                <PhoneInput
                  placeholder={signInText?.phoneNumber || 'Phone Number'}
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  countryCallingCodeEditable={false}
                  defaultCountry="US"
                />
              </div>
              {/* Guard against an implicit submit (Enter in the password field)
                  performing a native GET submission and reloading the page. */}
              <form className={cn(styles.fieldWrap, styles.fieldStack)} onSubmit={(e) => e.preventDefault()}>
                <Input
                  noLabel
                  className={styles.userInput}
                  // label={signUpText?.enterAPassword || 'Enter a password'}
                  minLength={12}
                  id="password-input"
                  value={password}
                  onInput={handlePasswordInput}
                  onPaste={(e) => {
                    const pastedText = (
                      e.clipboardData || window.clipboardData
                    ).getData('text');
                    if (pastedText.length > 30) {
                      e.preventDefault();
                    }
                    const totalLength = e.target.value.length + pastedText.length;
                    // Check if the total length (including pasted text) exceeds 30 characters
                    if (totalLength > 30) {
                      // alert('Password cannot exceed 30 characters.');
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const inputPassword = e.target.value;
                    if (inputPassword.length > 30) {
                      const trimmedText = inputPassword.slice(0, 30);
                      e.target.value = trimmedText;
                      setPassword(trimmedText);
                    }
                  }}
                  placeholder={signInText?.password || 'Password'}
                  type="SignUpPass"
                />
                <div />
              </form>
            </div>
          )}
          <AnimatePresence>
          {!accountExists && !noPassWord && continueToPassword && (
            <motion.div
              key="password-step"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: 'hidden' }}
            >
            <form
              className={cn(styles.passwordForm, styles.stepIn)}
              onSubmit={(e) => {
                e.preventDefault();
                saveEmailAndPasswordToSession();
                getMFAStatus();
              }}
            >
              <div className={styles.fieldWrap}>
              <Input
                className={styles.customuserInput}
                automaticFocus
                message={
                  signInText?.caseSensitive !== undefined
                    ? `(${signInText.caseSensitive})`
                    : '( Case Sensitive )'
                }
                minLength={8}
                maxLength={password && 30}
                isInvalid={isInvalid}
                onInput={handlePasswordInput}
                invalidErr={invalidErr}
                onPaste={(e) => {
                  const pastedText = (
                    e.clipboardData || window.clipboardData
                  ).getData('text');
                  if (pastedText.length > 30) {
                    e.preventDefault();
                  }
                  const totalLength = e.target.value.length + pastedText.length;
                  // Check if the total length (including pasted text) exceeds 30 characters
                  if (totalLength > 30) {
                    // alert('Password cannot exceed 30 characters.');
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const inputPassword = e.target.value;
                  if (inputPassword.length > 30) {
                    const trimmedText = inputPassword.slice(0, 30);
                    e.target.value = trimmedText;
                    setPassword(trimmedText);
                  }
                }}
                // onInput={(e) => {
                //   const { target: { value: inputNewPassword } } = e;
                //   if (inputNewPassword.length > 30) {
                //     alert('Password cannot exceed 30 characters.');
                //     setPassword(inputNewPassword.slice(0, 30));
                //   } else {
                //     setPassword(inputNewPassword);
                //   }
                // }}
                placeholder={signInText?.enterYourPassword || 'Enter your password'}
                type="password"
              />
              </div>
              <div>
                <Button
                  id="sasdasddd"
                  className={styles.primaryAction}
                  blockText
                  isBlue
                  isDisabled={
                    gettingMFAStatus || signingInUser || password === ''
                  }
                  isFullWidth
                  isLarge
                  submit
                >
                  {signingInUser
                    ? 'Signing In...' || `${signInText?.signingIn}...`
                    : signInText?.continue || 'Continue'}
                </Button>
              </div>
              <div className={styles.helperRow}>
                <Link
                  className={cn({
                    [authStyles.suggestionLink]: true,
                    [styles.forgotPasswordLink]: true,
                  })}
                  to={Pathname.authentication.forgotPassword}
                >
                  {signInText?.forgotPassword?.title || 'Forgot Password?'}
                </Link>
              </div>
            </form>
            </motion.div>
          )}
          </AnimatePresence>
          <input
            className={`${authStyles.hiddenFormField} ${styles.customuserInput}`}
            name="password"
            onChange={() => { }}
            type="password"
            value={password}
          />
          {/* <Collapse in={accountExists} timeout={transitionTimeout}>
            <Fade in={accountExists} timeout={transitionTimeout}>
              <div className={styles.newsletter}>
                <Checkbox
                  className={styles.newsletterCheckbox}
                  color="primary"
                  id="newsletter-sub"
                  onChange={({ target: { checked: termsAgreed } }) => {
                    setTermsAgreement(termsAgreed);
                  }}
                />
                <label
                  className={styles.newsletterText}
                  htmlFor="newsletter-sub"
                >
                  Yes! I would like to receive updates, special offers and other
                  information from Projector App.
                </label>
              </div>
            </Fade>
          </Collapse> */}
          {accountExists && (
            <>
              {/* <Collapse in={accountExists} timeout={transitionTimeout}>
                    <Fade in={accountExists} timeout={transitionTimeout}>
                      <div className={styles.reCaptchaContainer}>
                        <ReCAPTCHA
                          sitekey={GOOGLE_RECAPTCHA_KEY}
                          onChange={reCaptchaHandler}
                          theme="dark"
                        />
                      </div>
                    </Fade>
                  </Collapse> */}

              <div className={styles.termsContainer}>
                <div className={styles.terms}>
                  {!allPasswordConditionsMet && (
                    <PasswordRequirements password={password} />
                  )}
                  {allPasswordConditionsMet && (
                    <Collapse in={accountExists} timeout={transitionTimeout}>
                      <Fade in={accountExists} timeout={transitionTimeout}>
                        <ProjectorAcknowlegingTxt />
                      </Fade>
                    </Collapse>
                  )}
                </div>
                {/* Hidden Turnstile container: the first token was already
                    minted on the email screen. We keep an off-screen container
                    here so an API error can silently re-mint a fresh token
                    (tokens are single-use) without showing the widget. */}
                <div
                  ref={setTurnstileNode}
                  className={styles.turnstileHidden}
                />
                <div id="firebase-recaptcha-container" />
                <Button
                  className={styles.primaryAction}
                  blockText
                  isBlue
                  isDisabled={
                    !isNameFilled
                    || !phoneNumber
                    || !phoneNumber?.length
                    || signingUpUser
                    || updatingMobile
                    || requestingCode
                    || checkingAccountExistence
                    || !allPasswordConditionsMet
                    // Turnstile token is only needed for signUp. Once the
                    // account exists, a phone retry runs updateMobile (no token),
                    // so don't keep the button disabled on the burned token.
                    || (!signupCompleted && !cloudflareToken)
                    || !(
                      emailIsValid
                      && accountExists !== undefined
                      && accountExists === true
                    )
                  }
                  isFullWidth
                  isLarge
                  // submit
                  onClick={(e) => {
                    setPreferredEmail(email);
                    e.preventDefault();
                    // After signUp succeeded, retry only the phone save; otherwise
                    // create the account first.
                    if (signupCompleted) {
                      updateMobile();
                    } else {
                      signUpUser();
                    }
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (signupCompleted) {
                      updateMobile();
                    } else {
                      signUpUser();
                    }
                  }}
                >
                  {signInText?.agreeAndContinue || 'Agree and Continue'}
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <form
          className={styles.stepIn}
          onSubmit={(e) => {
            e.preventDefault();
            saveEmailAndPasswordToSession();
            getMFAStatus();
          }}
        >
          {/* <StepStatus currentStep="2" totalSteps="2" langText={signInText} /> */}
          <input
            className={authStyles.hiddenFormField}
            name="email"
            onChange={() => { }}
            type="email"
            value={email}
          />
          <div className={styles.fieldWrap}>
          <Input
            className={styles.customuserInput}
            label={signInText?.enterYourPassword || 'Enter your password'}
            message={
              signInText?.caseSensitive !== undefined
                ? `(${signInText.caseSensitive})`
                : '( Case Sensitivexx )'
            }
            minLength={8}
            maxLength={password && 30}
            isInvalid={isInvalid}
            onInput={handlePasswordInput}
            invalidErr={invalidErr}
            onPaste={(e) => {
              const pastedText = (
                e.clipboardData || window.clipboardData
              ).getData('text');
              if (pastedText.length > 30) {
                e.preventDefault();
              }
              const totalLength = e.target.value.length + pastedText.length;
              // Check if the total length (including pasted text) exceeds 30 characters
              if (totalLength > 30) {
                // alert('Password cannot exceed 30 characters.');
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const inputPassword = e.target.value;
              if (inputPassword.length > 30) {
                const trimmedText = inputPassword.slice(0, 30);
                e.target.value = trimmedText;
                setPassword(trimmedText);
              }
            }}
            // onInput={(e) => {
            //   const { target: { value: inputNewPassword } } = e;
            //   if (inputNewPassword.length > 30) {
            //     alert('Password cannot exceed 30 characters.');
            //     setPassword(inputNewPassword.slice(0, 30));
            //   } else {
            //     setPassword(inputNewPassword);
            //   }
            // }}
            placeholder={signInText?.password || 'Password'}
            type="password"
          />
          </div>
          <div>
            <Button
              id="sasdasddd"
              className={styles.primaryAction}
              blockText
              isBlue
              isDisabled={gettingMFAStatus || signingInUser}
              isFullWidth
              isLarge
              submit
            >
              {signingInUser
                ? 'Signing In...' || `${signInText?.signingIn}...`
                : signInText?.continue || 'Continue'}
            </Button>
          </div>
          <div className={styles.helperRow}>
            <Link
              className={cn({
                [authStyles.suggestionLink]: true,
                [styles.forgotPasswordLink]: true,
              })}
              to={Pathname.authentication.forgotPassword}
            >
              {signInText?.forgotPassword?.title || 'Forgot Password?'}
            </Link>
          </div>
        </form>
      )}
      </div>
      {pathname.includes('/newSign-in') && (
        <div className={styles.signupSuggest}>
          New to Projector?
          <span
            role="button"
            tabIndex={0}
            className={styles.signupSuggestLink}
            onClick={() => navigate(Pathname.authentication.newSignUp)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(Pathname.authentication.newSignUp); }}
          >
            Sign up
          </span>
        </div>
      )}
    </AuthPage>
  );
};

/**
 * Login page. Signed-out visitors always resolve to the classic layout (see
 * hooks/useNewUI) — the login screen has to look the same for everyone, since
 * we cannot know which account is about to sign in. Both variants share the
 * same logic; only the markup and stylesheet differ.
 */
export const NewSignIn = (props) => (
  isNewUIEnabled() ? <SignInNew {...props} /> : <SignInClassic {...props} />
);

export default NewSignIn;
